import { z } from "zod";
import { activateTwinProductVersionInputSchema } from "./twin-products.js";
import { convergenceProposalOperationSchema, convergenceProposalStatusSchema, convergenceProposalTargetKindSchema, } from "./convergence.js";
const safeIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9][a-z0-9._:-]*$/u);
export const twinOnboardingStepSchema = z.enum([
    "organisation",
    "connections",
    "discovery",
    "review",
    "publication",
    "activation",
    "complete",
]);
export const twinOnboardingJourneyStatusSchema = z.enum([
    "in_progress",
    "waiting_for_user",
    "blocked",
    "completed",
    "cancelled",
]);
export const twinOnboardingStepStatusSchema = z.enum([
    "pending",
    "in_progress",
    "waiting_for_user",
    "blocked",
    "completed",
    "skipped",
]);
export const twinOnboardingEventOutcomeSchema = z.enum([
    "started",
    "waiting_for_user",
    "blocked",
    "completed",
    "skipped",
    "resumed",
]);
export const twinOnboardingPlanInputSchema = z.object({
    goal: z.string().trim().min(1).max(2_000),
    connectionIds: z.array(safeIdSchema).max(50).default([]),
});
export const createTwinOnboardingJourneyInputSchema = twinOnboardingPlanInputSchema.extend({
    requestId: safeIdSchema,
});
export const continueTwinOnboardingJourneyInputSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    action: z
        .enum(["continue", "approve-review", "activate"])
        .default("continue"),
    approvedProposalIds: z.array(safeIdSchema).max(100).default([]),
    activation: activateTwinProductVersionInputSchema.optional(),
})
    .superRefine((value, context) => {
    if (value.action === "approve-review" &&
        value.approvedProposalIds.length === 0) {
        context.addIssue({
            code: "custom",
            path: ["approvedProposalIds"],
            message: "approve-review requires at least one proposal id",
        });
    }
    if (value.action === "activate" && !value.activation) {
        context.addIssue({
            code: "custom",
            path: ["activation"],
            message: "activate requires candidate evaluation authority",
        });
    }
    if (value.action !== "activate" && value.activation) {
        context.addIssue({
            code: "custom",
            path: ["activation"],
            message: "activation authority is only valid with the activate action",
        });
    }
});
export const twinOnboardingPlanStepSchema = z.object({
    step: twinOnboardingStepSchema,
    title: z.string().min(1),
    responsibility: z.enum(["agent", "person"]),
    status: twinOnboardingStepStatusSchema,
});
export const twinOnboardingPlanSchema = z.object({
    goal: z.string().min(1),
    connectionIds: z.array(safeIdSchema),
    steps: z.array(twinOnboardingPlanStepSchema),
});
export const twinOnboardingEventSchema = z.object({
    id: z.string().min(1),
    sequence: z.number().int().positive(),
    step: twinOnboardingStepSchema,
    outcome: twinOnboardingEventOutcomeSchema,
    actorId: z.string().min(1),
    code: safeIdSchema,
    createdAt: z.number().int().nonnegative(),
});
export const twinOnboardingReviewProposalSchema = z.object({
    id: safeIdSchema,
    title: z.string().trim().min(1).max(4_000),
    summary: z.string().trim().min(1).max(8_000),
    targetKind: convergenceProposalTargetKindSchema,
    operation: convergenceProposalOperationSchema,
    status: convergenceProposalStatusSchema,
});
export const twinOnboardingJourneySchema = z.object({
    id: z.string().min(1),
    tenantSlug: z.string().min(1),
    organisationId: z.string().min(1),
    goal: z.string().min(1),
    connectionIds: z.array(safeIdSchema),
    status: twinOnboardingJourneyStatusSchema,
    currentStep: twinOnboardingStepSchema,
    version: z.number().int().positive(),
    lastErrorCode: safeIdSchema.nullable(),
    createdByUserId: z.string().min(1),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
    completedAt: z.number().int().nonnegative().nullable(),
    discoveryRunId: z.string().min(1).nullable(),
    approvedProposalIds: z.array(safeIdSchema),
    publicationVersionIds: z.array(safeIdSchema),
    reviewProposals: z.array(twinOnboardingReviewProposalSchema),
    plan: twinOnboardingPlanSchema,
    events: z.array(twinOnboardingEventSchema),
});
export const twinOnboardingPlanResponseSchema = z.object({
    plan: twinOnboardingPlanSchema,
});
export const twinOnboardingJourneyResponseSchema = z.object({
    journey: twinOnboardingJourneySchema,
});
export const listTwinOnboardingJourneysResponseSchema = z.object({
    journeys: z.array(twinOnboardingJourneySchema),
});
export function buildTwinOnboardingPlan(input) {
    return {
        goal: input.goal,
        connectionIds: [...new Set(input.connectionIds)].sort(),
        steps: [
            {
                step: "organisation",
                title: "Confirm your organisation",
                responsibility: "agent",
                status: "completed",
            },
            {
                step: "connections",
                title: "Connect your business systems",
                responsibility: "agent",
                status: input.connectionIds.length > 0 ? "pending" : "skipped",
            },
            {
                step: "discovery",
                title: "Discover your first Twin",
                responsibility: "agent",
                status: "pending",
            },
            {
                step: "review",
                title: "Review what the Twin found",
                responsibility: "person",
                status: "pending",
            },
            {
                step: "publication",
                title: "Publish the approved Twin",
                responsibility: "agent",
                status: "pending",
            },
            {
                step: "activation",
                title: "Make the Twin available",
                responsibility: "agent",
                status: "pending",
            },
            {
                step: "complete",
                title: "Finish setup",
                responsibility: "agent",
                status: "pending",
            },
        ],
    };
}
export const onboardingPathParamsSchema = z.object({ journeyId: twinOnboardingJourneySchema.shape.id.optional() }).strict();
